<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch} from 'vue';
import $ from 'jquery';
import {refDebounced} from '@vueuse/core';

import {toggleHighlightElement} from './document_highlight.ts';
import {SearchTargetIndex, filterGroups, groupSearchTargets, type SearchTargetGroup} from './document-search.ts';
import {onNextFrame} from '../domutils.ts';
import type {Net} from './allspice_render/types.ts';
import {SvgIcon} from '../svg.ts';
import {clusterWires, groupWiresByNet, type NetWire} from '../utils/wire-cluster.ts';
import {groupResultsByCategory} from '../utils/search-grouping.ts';
import {isNetSearchDisabledSchematic} from '../utils/schematic-format.ts';

const FILE_TYPES = {
  PCB: 'pcb',
  SCHEMATIC: 'schematic',
} as const;
type FileType = typeof FILE_TYPES[keyof typeof FILE_TYPES];
type HighlightElementType = 'component' | 'net';

type SearchTarget = {
  category: string;
  dataSelector: string;
  description: string;
  elementType: HighlightElementType;
  pageId?: string | null;
  sourceSvg: SVGSVGElement | null;
  title: string;
};

type SchematicPageLike = {
  wires?: Record<string, NetWire>;
};

type SearchPageJson = {
  id?: string | null;
  name?: string;
  components?: Record<string, {reference?: string | null}>;
  wires?: Record<string, NetWire>;
};

type SearchPagesJson = {
  nets?: Record<string, Net>;
  pages?: SearchPageJson[] | null;
};

type IncompleteInspectorData = {
  pcbComponents: Record<string, {designator?: string}>;
  schComponents: {
    components?: Record<string, {reference?: string | null}>;
    pages?: SchematicPageLike[];
    wires?: Record<string, NetWire>;
  };
  nets: Record<string, Net>;
  allPagesHead?: SearchPagesJson | null;
  allPagesBase?: SearchPagesJson | null;
};

type PcbSearchResult = {
  category: string;
  dataSelector: string;
  elementType: HighlightElementType;
  title: string;
};

const props = defineProps<{
  isDiff: boolean;
  fileType: FileType;
  treePath?: string;
}>();

const searchRoot = useTemplateRef<HTMLElement>('searchRoot');
const searchContainer = useTemplateRef<HTMLElement>('searchContainer');
const searchField = useTemplateRef<HTMLInputElement>('searchField');

const expanded = ref(false);
const searchQuery = ref('');
const debouncedSearchQuery = refDebounced(searchQuery, 150);
const svg = ref<SVGSVGElement | null>(null);
const currentHighlightSelector = ref('');
const currentHighlightSvg = ref<SVGSVGElement | null>(null);
const currentHighlightElementType = ref<HighlightElementType>('component');
const searchIndex = new SearchTargetIndex<SearchTarget>();
const accumulatedSource = computed(() => searchIndex.targets);
const searchVersion = ref(0);
const selectedResultIndex = ref(0);
const selectedResultKey = ref<string | null>(null);

let searchBox: HTMLElement | null = null;

const isPcb = computed(() => props.fileType === FILE_TYPES.PCB);
const disableSchematicNetSearch = computed(() => isNetSearchDisabledSchematic(props.treePath ?? ''));
const placeholder = computed(() => disableSchematicNetSearch.value ? 'Find components...' : 'Find components or nets...');
const searchTooltip = computed(() => disableSchematicNetSearch.value ? 'Search for components' : 'Search for components and nets');
const groupedTargets = ref<SearchTargetGroup<SearchTarget>[]>([]);
const groupedTargetsByKey = computed(() => new Map(groupedTargets.value.map((g) => [g.key, g])));

// Only regroup when the search bar is open — avoids O(N) regrouping on every
// inspectordataload event while 100+ sub-page SVGs are loading.
watch([expanded, searchVersion, () => accumulatedSource.value.length], () => {
  if (expanded.value) {
    groupedTargets.value = groupSearchTargets(accumulatedSource.value);
  }
}, {flush: 'sync'});

const groupedResults = computed(() => filterGroups(groupedTargets.value, debouncedSearchQuery.value));
const selectedGroup = computed(() => {
  if (!selectedResultKey.value) {
    return null;
  }

  return groupedTargetsByKey.value.get(selectedResultKey.value) ?? null;
});
const selectedVisibleResult = computed(() => {
  if (!selectedResultKey.value) {
    return null;
  }

  return groupedResults.value.find((result) => result.key === selectedResultKey.value) ?? null;
});
const visibleResults = computed(() => {
  return selectedVisibleResult.value ? [selectedVisibleResult.value] : groupedResults.value;
});
const visibleResultsByCategory = computed(() => groupResultsByCategory(visibleResults.value));
const selectedTargets = computed(() => selectedGroup.value?.targets ?? []);
const totalTargets = computed(() => selectedTargets.value.length);
const selectedTargetIndex = computed(() => {
  if (totalTargets.value === 0) {
    return 0;
  }

  return Math.min(selectedResultIndex.value, totalTargets.value - 1);
});
const selectedTarget = computed(() => selectedTargets.value[selectedTargetIndex.value] ?? null);
const selectedTitle = computed(() => selectedGroup.value?.title ?? '');
const selectedDescription = computed(() => selectedTarget.value?.description ?? '');

function getSearchBox(): HTMLElement | null {
  if (searchBox?.isConnected) {
    return searchBox;
  }

  searchBox = searchRoot.value?.closest('.diff-file-box, .non-diff-file-content') as HTMLElement | null;
  return searchBox;
}

function getFallbackSvg(): SVGSVGElement | null {
  if (svg.value) {
    return svg.value;
  }

  const box = getSearchBox();
  return box?.querySelector<SVGSVGElement>('.svg-schematic svg.schematic, .svg-schematic svg.schematic_svg, .svg-wrapper svg') ?? null;
}

async function clearHighlight() {
  if (currentHighlightSelector.value !== '') {
    const targetSvg = currentHighlightSvg.value ?? getFallbackSvg();
    toggleHighlightElement(
      targetSvg,
      currentHighlightSelector.value,
      false,
      currentHighlightElementType.value,
      isPcb.value,
    );

    currentHighlightSelector.value = '';
    currentHighlightSvg.value = null;
    currentHighlightElementType.value = 'component';
  }

  await nextTick();
}

async function focusSearchField() {
  await nextTick();
  onNextFrame(() => {
    searchField.value?.focus();
  });
}

function setSelected(key: string | null, index: number) {
  selectedResultKey.value = key;
  selectedResultIndex.value = index;
}

async function clearSelection(shouldFocus = true) {
  setSelected(null, 0);
  await clearHighlight();
  if (shouldFocus && expanded.value) {
    await focusSearchField();
  }
}

function runInBackground(task: Promise<unknown>) {
  (async () => {
    try {
      await task;
    } catch {
      // Ignore background navigation/highlight failures from transient DOM state.
    }
  })();
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape' && expanded.value) {
    event.preventDefault();
    expanded.value = false;
    runInBackground(clearSelection(false));
  }
}

function handleMousedownOutside(event: MouseEvent) {
  if (!expanded.value || !searchRoot.value) {
    return;
  }

  if (!searchRoot.value.contains(event.target as Node)) {
    expanded.value = false;
    runInBackground(clearSelection(false));
  }
}

async function handleSearchIconClick(event: MouseEvent) {
  event.preventDefault();

  expanded.value = !expanded.value;
  if (expanded.value) {
    await focusSearchField();
    return;
  }

  if (!isPcb.value) {
    searchQuery.value = '';
  }
  if (searchField.value) {
    searchField.value.value = '';
  }

  await clearSelection(false);
}

function handlePreviewLoad(event: Event) {
  if (event.target instanceof SVGSVGElement) {
    svg.value = event.target;
    return;
  }

  if (event.target instanceof Element) {
    svg.value = event.target.closest('svg') as SVGSVGElement | null;
  }
}

function collectPcbSearchResults(inspectorData: IncompleteInspectorData): PcbSearchResult[] {
  const results: PcbSearchResult[] = [];

  for (const [componentId, component] of Object.entries(inspectorData.pcbComponents ?? {})) {
    if (!component.designator) {
      continue;
    }

    results.push({
      category: 'Designator',
      dataSelector: `[data-path^="component_instances/${CSS.escape(componentId)}"]`,
      elementType: 'component',
      title: component.designator,
    });
  }

  return results;
}

function collectNetSearchResults(inspectorData: IncompleteInspectorData): PcbSearchResult[] {
  return Object.values(inspectorData.nets ?? {}).map((net) => ({
    category: 'Net',
    dataSelector: `[data-net=${CSS.escape(net.id)}]`,
    elementType: 'net',
    title: net.name ?? net.id,
  }));
}

async function highlightSearchResult(
  result: {dataSelector: string; elementType: HighlightElementType},
  targetSvg: SVGSVGElement | null,
) {
  await clearHighlight();
  if (!targetSvg) {
    return;
  }

  toggleHighlightElement(
    targetSvg,
    result.dataSelector,
    true,
    result.elementType,
    isPcb.value,
  );
  currentHighlightSelector.value = result.dataSelector;
  currentHighlightSvg.value = targetSvg;
  currentHighlightElementType.value = result.elementType;

  await nextTick();
}

function handlePcbResultSelected(result: PcbSearchResult) {
  runInBackground(highlightSearchResult(result, getFallbackSvg()));

  document.body.dispatchEvent(new Event('inspector-hide'));
  document.body.dispatchEvent(new CustomEvent<{selector: string}>('dolly-zoom-request', {
    detail: {selector: result.dataSelector},
  }));
}

function handleResultsClose() {
  runInBackground(clearHighlight());
}

function initializePcbSearch(results: PcbSearchResult[]) {
  if (!searchContainer.value) {
    return;
  }

  $(searchContainer.value).search({
    type: 'category',
    fullTextSearch: 'exact',
    source: results,
    maxResults: 20,
    onSelect: handlePcbResultSelected,
    onResultsClose: handleResultsClose,
  });
}

function addComponentsFromPage(
  schData: IncompleteInspectorData['schComponents'] | undefined,
  sourceSvg: SVGSVGElement | null,
) {
  const components = schData?.components ?? {};
  for (const [componentId, componentData] of Object.entries(components)) {
    if (!componentData.reference) {
      continue;
    }

    searchIndex.addOrMerge({
      category: 'Reference',
      dataSelector: `g[data-id='${CSS.escape(componentId)}']`,
      description: '',
      elementType: 'component',
      sourceSvg,
      title: componentData.reference,
    });
  }

  searchVersion.value++;
}

function addNetsFromPages(
  schData: IncompleteInspectorData['schComponents'] | undefined,
  nets: Record<string, Net>,
  sourceSvg: SVGSVGElement | null,
) {
  // schData may be a single page (wires at top level) or the full JSON (pages[].wires).
  const pages: SchematicPageLike[] = schData?.pages ?? (schData?.wires ? [schData] : []);

  for (const page of pages) {
    // Group wires by net
    const wiresByNet = groupWiresByNet(page.wires ?? {}, nets);

    // For each net, cluster connected wires and create one SearchTarget per cluster
    for (const [netId, netWires] of wiresByNet) {
      const net = nets[netId];
      const clusters = clusterWires(netWires);

      for (const clusterIds of clusters) {
        const compoundSelector = clusterIds
          .map((id) => `[data-id='${CSS.escape(id)}']`)
          .join(', ');

        searchIndex.addOrMerge({
          category: 'Net',
          dataSelector: compoundSelector,
          description: '',
          elementType: 'net',
          sourceSvg,
          title: net.name ?? netId,
        });
      }
    }
  }

  searchVersion.value++;
}

function addComponentsFromAllPages(
  headJson: SearchPagesJson | null | undefined,
  baseJson: SearchPagesJson | null | undefined,
) {
  const seen = new Set<string>();

  for (const page of [...(headJson?.pages ?? []), ...(baseJson?.pages ?? [])]) {
    const pageId = page?.id ?? null;
    const pageName = page?.name ?? '';
    const components = page?.components ?? {};

    for (const [componentId, componentData] of Object.entries(components)) {
      if (!componentData.reference) {
        continue;
      }

      const dedupeKey = `${pageId ?? ''}::${componentId}`;
      if (seen.has(dedupeKey)) {
        continue;
      }

      seen.add(dedupeKey);
      searchIndex.addOrMerge({
        category: 'Reference',
        dataSelector: `g[data-id='${CSS.escape(componentId)}']`,
        description: pageName,
        elementType: 'component',
        pageId,
        sourceSvg: null,
        title: componentData.reference,
      });
    }
  }

  searchVersion.value++;
}

function addNetsFromAllPages(
  headJson: SearchPagesJson | null | undefined,
  baseJson: SearchPagesJson | null | undefined,
) {
  const nets: Record<string, Net> = {
    ...baseJson?.nets,
    ...headJson?.nets,
  };

  const allPages = [
    ...(headJson?.pages ?? []),
    ...(baseJson?.pages ?? []),
  ];

  for (const page of allPages) {
    const pageId = page?.id ?? null;
    const wiresByNet = groupWiresByNet(page.wires ?? {}, nets);

    for (const [netId, netWires] of wiresByNet) {
      const net = nets[netId];
      const clusters = clusterWires(netWires);

      for (const clusterIds of clusters) {
        const compoundSelector = clusterIds
          .map((id) => `[data-id='${CSS.escape(id)}']`)
          .join(', ');

        searchIndex.addOrMerge({
          category: 'Net',
          dataSelector: compoundSelector,
          description: '',
          elementType: 'net',
          pageId,
          sourceSvg: null,
          title: net.name ?? netId,
        });
      }
    }
  }

  searchVersion.value++;
}

function expandAccordionRow(pageId: string): Promise<void> {
  document.dispatchEvent(new CustomEvent('allspice-snippet-click-thru', {
    detail: {docId: pageId},
  }));

  const box = getSearchBox();
  return new Promise((resolve) => {
    function onReady(event: Event) {
      const detail = (event as CustomEvent).detail;
      if (detail?.allPagesHead !== undefined) return;
      // Look up the container inside the callback — it may not exist
      // until the accordion finishes expanding.
      const container = findPageContainer(box, pageId);
      if (container && event.target instanceof Node && !container.contains(event.target)) return;
      box?.removeEventListener('inspectordataload', onReady);
      resolve();
    }
    box?.addEventListener('inspectordataload', onReady);

    // Safety timeout so we don't hang forever.
    setTimeout(() => {
      box?.removeEventListener('inspectordataload', onReady);
      resolve();
    }, 10000);
  });
}

function handleInspectorDataLoad(event: Event) {
  const inspectorEvent = event as CustomEvent<IncompleteInspectorData>;

  if (isPcb.value) {
    initializePcbSearch([
      ...collectPcbSearchResults(inspectorEvent.detail),
      ...collectNetSearchResults(inspectorEvent.detail),
    ]);
    return;
  }

  const {allPagesHead, allPagesBase} = inspectorEvent.detail;
  if (allPagesHead?.pages || allPagesBase?.pages) {
    addComponentsFromAllPages(allPagesHead, allPagesBase);
    if (!disableSchematicNetSearch.value) {
      addNetsFromAllPages(allPagesHead, allPagesBase);
    }
    return;
  }

  const sourceSvg = event.target instanceof SVGSVGElement ?
    event.target :
    event.target instanceof Element ?
      event.target.closest('svg') as SVGSVGElement | null :
      null;

  const {schComponents, nets} = inspectorEvent.detail;
  addComponentsFromPage(schComponents, sourceSvg);
  if (!disableSchematicNetSearch.value) {
    addNetsFromPages(schComponents, nets, sourceSvg);
  }
}

function findPageContainer(box: ParentNode | null, pageId: string | null): HTMLElement | null {
  if (!box || !pageId) {
    return null;
  }

  const accordionHead = box.querySelector<HTMLElement>(`[data-accordion-head="${CSS.escape(pageId)}"]`);
  const accordionContent = accordionHead?.nextElementSibling;
  if (accordionContent instanceof HTMLElement) {
    return accordionContent;
  }

  return box.querySelector<HTMLElement>(`[data-accordion-content="${CSS.escape(pageId)}"]`);
}

function findTargetElement(result: SearchTarget, box: ParentNode | null): Element | null {
  if (!box) {
    return null;
  }

  if (result.pageId) {
    const pageContainer = findPageContainer(box, result.pageId);
    const pageElement = pageContainer?.querySelector(result.dataSelector) ?? null;
    if (pageElement) {
      return pageElement;
    }
  }

  return box.querySelector(result.dataSelector) ?? null;
}

async function highlightAndZoom(result: SearchTarget, targetEl: Element | null) {
  let targetSvg = result.sourceSvg ?? svg.value;
  if (targetEl) {
    targetSvg = (targetEl.closest('svg') as SVGSVGElement | null) ?? targetSvg;
  }

  if (!targetSvg) {
    return;
  }

  // For nets, highlight ALL wires of the net (compound selector from group).
  const highlightSelector = result.elementType === 'net' ?
    selectedTargets.value.map((t) => t.dataSelector).join(', ') :
    result.dataSelector;

  await highlightSearchResult(
    {dataSelector: highlightSelector, elementType: result.elementType},
    targetSvg,
  );

  // Dispatch dolly-zoom-request to zoom to the target.
  // For nets, zoom to the current cluster (instance), not the full net.
  document.body.dispatchEvent(new Event('inspector-hide'));
  document.body.dispatchEvent(new CustomEvent<{selector: string}>('dolly-zoom-request', {
    detail: {selector: result.dataSelector},
  }));
}

async function navigateToTarget(result: SearchTarget) {
  await clearHighlight();

  const box = getSearchBox();
  let targetEl = findTargetElement(result, box);

  // If the element isn't in the DOM and we know the page, expand its accordion.
  if (!targetEl && result.pageId) {
    await expandAccordionRow(result.pageId);

    // The SVG may still be painting after the accordion expands.
    // Poll each frame until found or a 2 s budget is exhausted.
    const deadline = performance.now() + 2000;
    while (!targetEl && performance.now() < deadline) {
      await new Promise((r) => requestAnimationFrame(r));
      targetEl = findTargetElement(result, box);
    }
  }

  // Scroll the target's accordion row into view (whether just expanded or already open).
  if (result.pageId) {
    const row = box?.querySelector(`[data-accordion-head="${CSS.escape(result.pageId)}"]`);
    row?.scrollIntoView({behavior: 'smooth', block: 'start'});
  } else if (targetEl) {
    targetEl.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  await highlightAndZoom(result, targetEl);
}

async function onResultClick(result: (typeof visibleResults.value)[number]) {
  const target = result.targets[0] ?? null;
  if (!target) {
    return;
  }

  setSelected(result.key, 0);
  await navigateToTarget(target);
}

async function selectTargetAt(index: number) {
  if (!selectedResultKey.value || totalTargets.value === 0) {
    return;
  }

  setSelected(selectedResultKey.value, index);
  await navigateToTarget(selectedTargets.value[index]!);
}

async function nextInstance() {
  if (totalTargets.value === 0) {
    return;
  }

  const newIndex = (selectedTargetIndex.value + 1) % totalTargets.value;
  await selectTargetAt(newIndex);
}

async function prevInstance() {
  if (totalTargets.value === 0) {
    return;
  }

  const newIndex = (selectedTargetIndex.value - 1 + totalTargets.value) % totalTargets.value;
  await selectTargetAt(newIndex);
}

async function focusSelectedInstance() {
  if (totalTargets.value === 0) {
    return;
  }

  await selectTargetAt(selectedTargetIndex.value);
}

watch(searchQuery, (query, previousQuery) => {
  if (query !== previousQuery && selectedResultKey.value) {
    runInBackground(clearSelection(false));
  }
});

watch(expanded, (isExpanded) => {
  if (isExpanded) {
    // Capture phase so the dismiss fires before bubble-phase SVG click
    // handlers that might re-trigger or interfere with the search state.
    document.addEventListener('mousedown', handleMousedownOutside, true);
  } else {
    document.removeEventListener('mousedown', handleMousedownOutside, true);
  }
});

onMounted(() => {
  const box = getSearchBox();
  box?.addEventListener('inspectordataload', handleInspectorDataLoad as EventListener);
  box?.addEventListener('previewload', handlePreviewLoad as EventListener);
  document.body.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  runInBackground(clearHighlight());
  const box = getSearchBox();
  box?.removeEventListener('inspectordataload', handleInspectorDataLoad as EventListener);
  box?.removeEventListener('previewload', handlePreviewLoad as EventListener);
  document.body.removeEventListener('keydown', handleEscape);
  document.removeEventListener('mousedown', handleMousedownOutside, true);
});

defineExpose({
  accumulatedSource,
  expanded,
  groupedResults,
  navigateToTarget,
  searchIndex,
  searchQuery,
  selectedResultKey,
  selectedTargetIndex,
  selectedTargets,
  totalTargets,
  visibleResults,
});
</script>

<template>
  <div
    ref="searchRoot"
    class="document-search search"
  >
    <a
      style="text-decoration: none;"
      class="search-toggle-button"
      @click="handleSearchIconClick"
    >
      <span class="btn-octicon tooltip" :data-tooltip-content="searchTooltip" data-position="bottom-center" data-variation="inverted tiny">
        <i class="icon tw:flex tw:items-center tw:justify-center" style="padding-left: 8px"><svg-icon name="octicon-search" :size="16"/></i>
      </span>
    </a>
    <div
      v-show="expanded"
      :class="['search-bar', { 'search-bar-pcb': isPcb }]"
    >
      <template v-if="!isPcb">
        <div
          v-if="selectedResultKey && totalTargets > 0"
          class="iterate-controls"
        >
          <button
            v-if="totalTargets > 1"
            type="button"
            class="ui mini basic icon button"
            :disabled="totalTargets <= 1"
            @click="prevInstance"
          >
            <svg-icon
              name="octicon-chevron-left"
              :size="14"
            />
          </button>
          <button
            type="button"
            class="iterate-label iterate-label-button"
            data-test="selected-instance"
            aria-label="Show selected component"
            @click="focusSelectedInstance"
          >
            <div class="tw:font-mono">
              {{ selectedTitle }}
            </div>
            <div class="tw:text-text-light-1 tw:text-xs">
              <template v-if="totalTargets > 1">
                {{ selectedTargetIndex + 1 }} of {{ totalTargets }}<template v-if="selectedDescription"> — {{ selectedDescription }}</template>
              </template>
              <template v-else>
                {{ selectedDescription }}
              </template>
            </div>
          </button>
          <button
            v-if="totalTargets > 1"
            type="button"
            class="ui mini basic icon button"
            :disabled="totalTargets <= 1"
            @click="nextInstance"
          >
            <svg-icon
              name="octicon-chevron-right"
              :size="14"
            />
          </button>
        </div>
        <div class="search-input-wrapper">
          <input
            ref="searchField"
            v-model="searchQuery"
            tabindex="-1"
            class="search-prompt"
            type="text"
            :placeholder="placeholder"
          >
        </div>
        <div
          v-if="debouncedSearchQuery && visibleResults.length > 0"
          class="search-results"
        >
          <template v-for="group in visibleResultsByCategory" :key="group.category">
            <div class="search-category-name">
              {{ group.category }}
            </div>
            <div class="search-results-list">
              <div
                v-for="result in group.results"
                :key="result.key"
                class="search-result"
                @click="onResultClick(result)"
              >
                <div class="search-result-title">
                  {{ result.title }}
                </div>
                <div
                  v-if="result.description"
                  class="search-result-description"
                >
                  {{ result.description }}
                </div>
              </div>
            </div>
          </template>
        </div>
        <div
          v-else-if="debouncedSearchQuery && accumulatedSource.length > 0"
          class="search-no-results"
        >
          No results found.
        </div>
      </template>
      <div
        v-else
        ref="searchContainer"
        class="pcb-search-container ui scrolling category loading search"
      >
        <div class="ui icon input">
          <input
            ref="searchField"
            tabindex="-1"
            class="prompt"
            type="text"
            :placeholder="placeholder"
          >
        </div>
        <div class="results"/>
      </div>
    </div>
  </div>
</template>

<style scoped>
.document-search {
  position: relative;
}

.search-bar {
  top: 1.5rem;
  right: -0.5rem;
  position: absolute;
  z-index: 999;
  width: min(28em, calc(100vw - 24px));

  &.search-bar-pcb {
    width: auto;
  }
}
.search-input-wrapper {
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  padding: 4px;
  width: 100%;
  box-sizing: border-box;
}
.search-prompt {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  background: var(--color-body);
  color: var(--color-text);
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}
.search-prompt:focus {
  border-color: var(--color-primary);
}
.search-results {
  width: 100%;
  max-height: 60vh;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  margin-top: 2px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
}
.search-category-name {
  padding: 10px 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-light);
  background: var(--color-body);
  border-right: 1px solid var(--color-secondary);
  font-size: 12px;
}
.search-category-name ~ .search-category-name,
.search-category-name ~ .search-category-name + .search-results-list {
  border-top: 1px solid var(--color-secondary);
}
.search-results-list {
  min-width: 0;
  max-height: 60vh;
  overflow-y: auto;
}
.search-result {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--color-secondary);
}
.search-result:last-child {
  border-bottom: none;
}
.search-result:hover {
  background: var(--color-secondary);
}
.search-result-title {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}
.search-result-description {
  font-size: 12px;
  color: var(--color-text-light-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-no-results {
  width: 100%;
  padding: 12px;
  text-align: center;
  color: var(--color-text-light-1);
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  margin-top: 2px;
  box-sizing: border-box;
}
.iterate-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.iterate-label {
  flex: 1;
  text-align: center;
}
.iterate-label-button {
  min-width: 0;
  padding: 4px 6px;
  border: 0;
  border-radius: var(--border-radius);
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.iterate-label-button:hover {
  background: var(--color-hover);
}
</style>

<style scoped>
/* stylelint-disable selector-pseudo-class-no-unknown */
.search-bar :deep(.ui.category.search .results) {
  width: 100%;
}
.search-bar :deep(.ui.search > .results) {
  background: var(--color-body) !important;
  border-color: var(--color-secondary) !important;
}
.search-bar :deep(.ui.search .results .result),
.search-bar :deep(.ui.search .results .result.active) {
  background: var(--color-body) !important;
  border-color: var(--color-secondary) !important;
}
.search-bar :deep(.ui.search .results .result .title),
.search-bar :deep(.ui.search .results .result.active .title) {
  color: var(--color-text) !important;
}
.search-bar :deep(.ui.search .results .result:hover) {
  background: var(--color-secondary) !important;
}
.search-bar :deep(.ui.search .results .category .name) {
  color: var(--color-text-light) !important;
  background: var(--color-body) !important;
}
/* stylelint-enable selector-pseudo-class-no-unknown */
</style>
